//------------------------------------------------------ externos
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
//------------------------------------------------------ funciones
import { PrivateRoute, PublicRoute } from "../functions/routesFunctions";
import { iniciarRealtime } from "../services/store";
import { useAuth } from "../auth/AuthContext";
//------------------------------------------------------ elementos
import Viajes from "../sections/Viajes";
import ViajeDetalle from "../sections/ViajeDetalle";
// Los temas: elige el armazón (menú al costado o arriba) y pone el tema. Con
// «Estándar» —el de arranque— es el `Layout` de siempre, sin tema.
import LayoutSwitcher from "../components/layout/LayoutSwitcher";
import MarcoAbierto from "../components/layout/MarcoAbierto";
import AvisoActualizacion from "../components/layout/AvisoActualizacion";
// CRT y MIC entran con la versión al día (Fabián e Igna, 09/10). Va en el
// paquete principal y no con la pantalla: tiene que poder mirar ANTES de que
// se pida el archivo de la pantalla.
import AlDiaParaEmitir from "../components/layout/AlDiaParaEmitir";
import Novedades from "../sections/Novedades";
import Resources from "../sections/Resources";
import Satelital from "../sections/Satelital";
import Porteria from "../sections/Porteria";
import Deposits from "../sections/Deposits";
import Login from "./Login";
import CambiarPassword from "./CambiarPassword";
import AprobarQr from "./AprobarQr";
import AvisoLlave from "../components/acceso/AvisoLlave";
import Ayuda from "./Ayuda";
import ComoSeUsa from "./ComoSeUsa";
import CrearClave from "./CrearClave";
import Padron from "./Padron";
import Permisos from "./Permisos";
import Tickets from "./Tickets";
import Herramientas from "./Herramientas";
import CajasHerramientas from "./CajasHerramientas";
import CajaHerramientas from "./CajaHerramientas";
import HerramientasAdmin from "./HerramientasAdmin";
import ConstanciaEntrega from "./ConstanciaEntrega";
import { RUTA_ELEMENTOS } from "../shared/entregasPanol";
import Mensajes from "./Mensajes";
import RTO from "./RTO";
import SubirPlanilla from "./SubirPlanilla";
import SubirCargas from "./SubirCargas";
import SatelitalPosiciones from "./SatelitalPosiciones";
import SatelitalCompletar from "./SatelitalCompletar";
import SatelitalTorrecillas from "./SatelitalTorrecillas";
import OrdenGasoil from "./OrdenGasoil";
import Multas from "./Multas";
import Documentacion from "./Documentacion";
import PuertoFichaje from "./PuertoFichaje";
import ChoferesAutorizados from "./ChoferesAutorizados";
import PuertoPanol from "./PuertoPanol";
import PuertoTrabajos from "./PuertoTrabajos";
import PuertoNovedades from "./PuertoNovedades";
import FueguinoCargas from "./FueguinoCargas";
import FueguinoTarifas from "./FueguinoTarifas";
import MiEspacio from "./MiEspacio";
import Automatizaciones from "./Automatizaciones";
import Automatizacion from "./Automatizacion";
import MisDocumentos from "./MisDocumentos";
import MisPendientes from "./MisPendientes";
import Visitas from "./Visitas";
import Llaves from "./Llaves";
import LlavesEtiquetas from "./LlavesEtiquetas";
import Llave from "./Llave";
import Vigilancia from "./Vigilancia";
import PedidosSistema from "./PedidosSistema";
import Furgones from "./Furgones";
import Choferes from "./Choferes";
import Nomina from "./Nomina";
import Diferencias from "./Diferencias";
import Licencias from "./Licencias";
import RrhhNovedades from "./RrhhNovedades";
import Infracciones from "./Infracciones";
import Vacaciones from "./Vacaciones";
import VacacionesImportar from "./VacacionesImportar";
import ArtSeguimiento from "./ArtSeguimiento";
import Urea from "./Urea";
import Gasoil from "./Gasoil";
import Accesos from "./Accesos";
import Alertas from "./Alertas";
import Lecturas from "./Lecturas";
import FacturasRemitos from "./FacturasRemitos";
import FacturasAPagar from "./FacturasAPagar";
import Persona from "./Persona";
import Unidad from "./Unidad";
//------------------------------------------------------ estilos
import "../assets/css/App.css";

/* PRESIDENCIA SE CARGA APARTE, y es la única pantalla del hub que lo hace.
 *
 * Es la que trae recharts, que pesa más que varias secciones juntas — y la
 * miran DOS personas. Importada como todas las demás, esos kilobytes los
 * descarga también el de portería que abre el hub cinco veces por día y nunca
 * va a ver un gráfico. Así los paga sólo quien entra al tablero. */
const Presidencia = lazy(() => import("./Presidencia"));

/* YPF también va aparte: trae recharts para el escalón de lo que declara
   Carlos Becerra, lo mismo que pesa en Presidencia. Lo mira quien sigue el
   precio del gasoil, no el de portería. */
const Ypf = lazy(() => import("./Ypf"));

/* Y el taller de PDFs, por lo mismo: trae `pdf-lib`, que es el motor que arma
 * los PDFs y pesa lo suyo. Lo usa quien tiene que juntar dos escaneos, no el
 * de portería que abre el hub cinco veces por día. Así esos kilobytes los baja
 * el que entra. */
const Pdfs = lazy(() => import("./Pdfs"));
const Aduana = lazy(() => import("./Aduana"));
const Crt = lazy(() => import("./Crt"));
const Mic = lazy(() => import("./Mic"));
const CrtUat = lazy(() => import("./CrtUat"));
const AduanaPapeles = lazy(() => import("./AduanaPapeles"));
const Seguros = lazy(() => import("./Seguros"));
const Garantias = lazy(() => import("./Garantias"));
const PedidosChoferes = lazy(() => import("./PedidosChoferes"));
/* El detalle de un ticket del taller con sus repuestos colocados: lo abre el
 * taller, no el que entra cinco veces por día a otra cosa. */
const TicketTaller = lazy(() => import("./TicketTaller"));
const FueguinoAref = lazy(() => import("./FueguinoAref"));
const EspejoChats = lazy(() => import("./EspejoChats"));
const NoExiste = lazy(() => import("./NoExiste"));

function App() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // cargar listados realtime → eventos, personas, vehiculos(tr/fg/vh), stock
    // ojo: tiene que esperar a que haya sesión confirmada. Si se dispara
    // antes (sin sesión todavía), Supabase corre la consulta como "anon",
    // RLS la filtra a 0 filas (sin error visible) y como nunca se vuelve
    // a pedir, queda vacío para siempre aunque despues termines de logear.
    if (!isAuthenticated) return;

    const unsuscribe = iniciarRealtime();

    return () => {
      unsuscribe?.();
    };
  }, [isAuthenticated]);
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      {/* El cartel de "hay versión nueva": va arriba de todo y sobre cualquier
          pantalla, sin interrumpir ni forzar recarga. */}
      <AvisoActualizacion />
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Crear la contraseña desde el enlace del correo.
            Va SIN PublicRoute a propósito: el enlace de recuperación abre
            sesión, así que PublicRoute la daría por logueada y la mandaría a
            "/" — nunca vería el formulario, que es justo lo que vino a hacer.

            /reset-password es el destino al que apuntaban los correos que ya
            se enviaron. Esa ruta nunca existió, así que esos enlaces morían en
            una pantalla en blanco; queda como alias para que sigan sirviendo. */}
        <Route path="/crear-clave" element={<CrearClave />} />
        <Route path="/reset-password" element={<CrearClave />} />

        {/* ↓↓ --------------- Rutas Abiertas ↓↓
            Se leen SIN CUENTA. Hoy es una sola: `/ayuda` («Primeros pasos» en
            el menú; no confundir con el sector «¿Cómo se usa?», que es
            `/como-se-usa` y pide sesión), que es el enlace que va en el mail
            con la clave provisoria — o sea que quien lo abre todavía no entró
            nunca. Estaba adentro del grupo privado y
            el guard lo mandaba al login: la pantalla que explica cómo entrar
            pidiendo estar adentro.

            `MarcoAbierto` elige el marco según haya sesión o no, así que al
            que ya entró se le sigue dibujando el hub entero con su menú. */}
        <Route element={<MarcoAbierto />}>
          <Route path="/ayuda" element={<Ayuda />} />
        </Route>

        {/* ↓↓ --------------- Rutas Privadas ↓↓ */}
        <Route
          element={
            <PrivateRoute>
              <LayoutSwitcher />
              {/* «Generá una llave de acceso», después de entrar en una compu. */}
              <AvisoLlave />
            </PrivateRoute>
          }
        >
          {/* LA HOME ES NOVEDADES. Antes era un saludo con la hora: una
              pantalla entera para decir "buenas tardes" y qué hora es, que es
              justo lo que ya dice el teléfono. La primera pantalla del día
              tiene que decir qué hay que hacer hoy — si no, se pasa de largo
              y el hub empieza en el menú. */}
          <Route path="/" element={<Novedades />} />
          {/* La dirección vieja sigue viva: se mandó por mail y por WhatsApp.
              `replace` para que el botón de atrás no rebote entre las dos. */}
          <Route path="/novedades" element={<Navigate to="/" replace />} />
          <Route path="/cambiar-password" element={<CambiarPassword />} />
          {/* El celular aprueba el QR de la compu (ingreso sin clave, 02/11). */}
          <Route path="/qr/:token" element={<AprobarQr />} />
          <Route path="/recursos" element={<Resources />} />
          <Route path="/porteria" element={<Porteria />} />
          <Route path="/satelital" element={<Satelital />} />
          {/* Subir un Excel de internos y bajarlo con la ubicación actual
              (Leonel, 23/09). Cuelga de Satelital: mismo sector, mismo permiso. */}
          <Route path="/satelital/posiciones" element={<SatelitalPosiciones />} />
          {/* Arribos y Salidas (Leonel, #267): la misma herramienta que
              Ubicaciones, con su propia entrada para llevar cada lista aparte
              sin pisar la de ubicaciones. */}
          <Route path="/satelital/arribos" element={<SatelitalPosiciones titulo="Arribos" sub="Tu lista de arribos, subila cuando quieras verla ubicada." />} />
          <Route path="/satelital/salidas" element={<SatelitalPosiciones titulo="Salidas" sub="Tu lista de salidas, subila cuando quieras verla ubicada." />} />
          {/* Ticket #421 (Leonel, 15/10): subir un Excel con huecos y bajarlo
              completo con los datos de la empresa. Mismo sector y permiso. */}
          <Route path="/satelital/completar" element={<SatelitalCompletar />} />
          <Route path="/satelital/torrecillas" element={<SatelitalTorrecillas />} />
          {/* Ticket 431 (Lautaro, 16/10): la orden de gas oil para una YPF con
              cuenta corriente, como el correo de los enganches. */}
          <Route path="/satelital/orden-gasoil" element={<OrdenGasoil />} />

          <Route path="/depositos" element={<Deposits />} />
          <Route path="/viajes" element={<Viajes />} />
          <Route path="/viajes/:id" element={<ViajeDetalle />} />
          {/* El panel de FlotaBot lo dibuja el armazón, no esta ruta: así los
              iframes sobreviven cuando alguien se va a otra pantalla y vuelve,
              en vez de recargar el mapa entero cada vez. La ruta existe para
              que la dirección funcione y el menú marque dónde está. */}
          {/* «Entrega de elementos» era un panel de FlotaBot y pasó a ser parte
              de Herramientas (P-311, Nico: «deberían ser la misma»). Las
              direcciones viejas —un favorito, un link en un mensaje— caen en
              las cajas de los choferes en vez de en un panel que ya no está. */}
          <Route path="/panel/elementos" element={<Navigate to={RUTA_ELEMENTOS} replace />} />
          <Route path="/elementos" element={<Navigate to={RUTA_ELEMENTOS} replace />} />
          <Route path="/panel/:seccion" element={null} />
          {/* Los listados como pantalla propia. Antes sólo se llegaba a ellos
              desde adentro de otra sección, en un modal. */}
          <Route path="/padron/:que" element={<Padron />} />
          {/* Quién puede qué. La pantalla decide qué dibujar; quién puede de
              verdad lo decide la base, que verifica en cada llamada. */}
          <Route path="/permisos" element={<Permisos />} />
          {/* La ticketera no pide permiso para entrar: cualquiera con sesión
              puede pedir algo. Qué tickets ve y cuáles puede atender lo decide
              la base, fila por fila. */}
          <Route path="/tickets" element={<Tickets />} />
          {/* El tablero de herramientas del taller. Ver pide `ver.taller`;
              prestar y devolver piden `cargar.taller`, y esa diferencia la
              hace la pantalla adentro: tráfico puede mirar qué falta sin
              poder mover nada. */}
          {/* Nico, 02/10: Herramientas arranca en las cajas de cada persona;
              la pared compartida es el pañol de préstamos. */}
          <Route path="/taller/herramientas" element={<CajasHerramientas />} />
          <Route path="/taller/herramientas/caja/:dni" element={<CajaHerramientas />} />
          <Route path="/taller/herramientas/panol" element={<Herramientas />} />
          <Route path="/taller/herramientas/administrar" element={<HerramientasAdmin />} />
          {/* P-311: el papel de cada entrega, para imprimir, firmar y subir. */}
          <Route path="/taller/herramientas/constancia/:id" element={<ConstanciaEntrega />} />
          {/* La libreta y los mensajes internos. Tampoco pide permiso: poder
              preguntarle algo a un compañero no es un privilegio. */}
          <Route path="/mensajes" element={<Mensajes />} />
          {/* El RTO de los camiones, traído del CENT. Dos usos en una: el
              veredicto del portón y la lista de lo que hay que renovar. */}
          <Route path="/rto" element={<RTO />} />
          {/* Subir la planilla de movimientos (Aldo, 30/08). Cuelga de
              Viajes: es la misma persona haciendo el mismo trabajo por
              otro camino. */}
          <Route path="/subir-planilla" element={<SubirPlanilla />} />
          <Route path="/subir-cargas" element={<SubirCargas />} />
          <Route path="/multas" element={<Multas />} />
          {/* El sector «¿Cómo se usa?»: todas las guías que le tocan a quien
              mira. La lista la arma FlotaBot, recortada por permiso. */}
          <Route path="/como-se-usa" element={<ComoSeUsa />} />
          {/* La guía de Multas que vivía acá quedó sin ningún enlace que
              llevara a ella (el de Multas salió con #552) y repetía la que
              sirve FlotaBot en su `/como/multas`, que es la que lista «¿Cómo se
              usa?». La dirección vieja cae ahí, en vez de en «no existe». */}
          <Route path="/como/multas" element={<Navigate to="/como-se-usa" replace />} />
          {/* Los furgones van aparte: de ellos sólo sabemos el RTO — no hay
              GPS, ni kilómetros, ni viajes cargados —, así que la pantalla es
              un listado y no una ficha con posición como la de tractores. */}
          <Route path="/furgones-rto" element={<Furgones />} />
          {/* Los choferes, sacados de la nómina de sueldos y no del padrón.
              ES OTRA LISTA Y NO UN FILTRO de `/padron/personas`: aquélla son
              2.476 filas cuyo `estado` heredado de Firestore da 356 activos,
              con DNI inventados y bajas que nadie registró adentro. Ésta son
              los 170 que cobran como chofer este mes. */}
          <Route path="/choferes" element={<Choferes />} />
          {/* RRHH. SON DOS PANTALLAS Y NO UNA SOLAPA de `/choferes`: aquélla
              vive en Padrones y la abre cualquiera con `ver.gente` —nombre,
              empresa y base, que es lo que tráfico necesita para armar un
              viaje—. Éstas piden `ver.rrhh`, que hoy tienen dos personas, y
              muestran lo que no está en ningún otro lado del hub: antigüedad,
              encuadre, contrato y los motivos de egreso cuando lleguen.

              Colgarlas de la misma pantalla obligaría a que una sola ruta
              tuviera dos permisos según la solapa abierta, que es la clase de
              cosa que un día se configura mal y nadie nota. */}
          <Route path="/rrhh/nomina" element={<Nomina />} />
          <Route path="/rrhh/diferencias" element={<Diferencias />} />
          <Route path="/rrhh/licencias" element={<Licencias />} />
          <Route path="/rrhh/novedades" element={<RrhhNovedades />} />
          <Route path="/rrhh/infracciones" element={<Infracciones />} />
          <Route path="/rrhh/vacaciones" element={<Vacaciones />} />
          <Route path="/rrhh/vacaciones/importar" element={<VacacionesImportar />} />
          <Route path="/rrhh/art" element={<ArtSeguimiento />} />
          {/* YPF: el gasoil D-500 que declara Carlos Becerra. Lazy por recharts. */}
          <Route
            path="/ypf"
            element={
              <Suspense fallback={<p className="tenue">Consultando…</p>}>
                <Ypf />
              </Suspense>
            }
          />
          {/* Fueguino: las encomiendas, vistas desde la oficina. La carga y el
              escaneo viven en la PWA `encomiendas.fibot.ar`, que trabaja sin
              señal porque en Río Grande el internet se corta; acá se mira todo
              junto. Sin `lazy`: la pantalla no trae ninguna librería pesada. */}
          <Route path="/fueguino/cargas" element={<FueguinoCargas />} />
          {/* AREF autocompletar: las fojas de ruta de Cristal → las filas del
              AREF. Lazy porque trae XLSX (SheetJS), que no hace falta en el
              arranque del hub. Corre entero en el navegador. */}
          <Route
            path="/fueguino/aref"
            element={
              <Suspense fallback={<p className="tenue">Abriendo…</p>}>
                <FueguinoAref />
              </Suspense>
            }
          />
          {/* Tarifas: la escala de la carta de TransAmerica + un cotizador
              (marcado borrador hasta que Matías confirme la regla). Sin lazy:
              son datos y una cuenta, no trae librería pesada. */}
          <Route path="/fueguino/tarifas" element={<FueguinoTarifas />} />
          {/* Mi espacio: la planilla personal de cada usuario (estilo Excel con
              fórmulas). Sin llave: existe para todos y la RLS recorta a lo tuyo. */}
          <Route path="/mi-espacio" element={<MiEspacio />} />
          <Route path="/mi-espacio/documentos" element={<MisDocumentos />} />
          <Route path="/mi-espacio/pendientes" element={<MisPendientes />} />
          {/* Automatizaciones (Igna, 03/10/2026): «cuando pasa esto, hacé
              esto», con bloques. Sin llave: todos ven todas y cada uno toca
              lo suyo (lo exige la RLS). `nueva` va antes que `:id` sólo por
              claridad: react-router elige la ruta estática igual. */}
          <Route path="/automatizaciones" element={<Automatizaciones />} />
          <Route path="/automatizaciones/nueva" element={<Automatizacion />} />
          <Route path="/automatizaciones/:id" element={<Automatizacion />} />
          {/* El tablero del presidente. Ruta propia y permiso propio: no es un
              resumen operativo, es la foto comercial de la empresa, y hoy la
              tienen dos personas. */}
          <Route
            path="/presidencia"
            element={
              /* El fallback dice «Consultando…» y no queda en blanco: el
                 tablero ya tarda lo suyo en pedir los datos, y dos esperas
                 seguidas sin una sola palabra se leen como que no cargó. */
              <Suspense fallback={<p className="tenue">Consultando…</p>}>
                <Presidencia />
              </Suspense>
            }
          />
          {/* LA FICHA DE UNA UNIDAD, que junta en un lugar lo que hasta hoy
              estaba repartido en cinco pantallas: el RTO con su turno, la
              última ubicación, el taller, el último cruce del portón y el
              AdBlue.

              NO ESTÁ EN EL MENÚ, y no es un olvido: no es una pantalla a la
              que se va, es a donde se LLEGA — desde el buscador, desde el RTO
              o desde un padrón. Ponerla en el rail obligaría a elegir una
              unidad antes de tener una, que es al revés de como se usa. Por
              eso tampoco lleva permiso propio: cada bloque cuelga del de su
              pantalla, igual que las tarjetas de la home.

              El tipo va en la dirección porque **un interno no identifica un
              vehículo**: 430 números existen en las dos tablas, así que
              `/unidad/774` a secas tendría que adivinar, y en la mitad de los
              casos mostraría el camión equivocado con total confianza. */}
          <Route path="/unidad/:tipo/:interno" element={<Unidad />} />
          {/* Y la de una persona, que es su gemela: junta el último cruce del
              portón, el teléfono cruzado entre el padrón y la planilla, y los
              vencimientos. Tampoco está en el menú, por lo mismo — es a donde
              se LLEGA, desde el buscador o desde el padrón.

              A diferencia de la unidad, ÉSTA SÍ PIDE PERMISO (`ver.gente`): el
              DNI y el teléfono de una persona no están abiertos en ninguna
              otra pantalla del hub, así que pedir menos que el padrón sería
              abrir por la ventana lo que la puerta tiene con llave. */}
          <Route path="/persona/:dni" element={<Persona />} />
          {/* La documentación de los choferes. Vive acá y no en el panel del
              bot porque ESCRIBE: hasta hoy los vencimientos sólo se podían
              corregir abriendo el Excel o mandándole un mensaje al bot. */}
          <Route path="/documentacion" element={<Documentacion />} />
          {/* PUERTO. Las dos son de MIRAR: lo que se carga entra por WhatsApp
              —la foto del molinete y el trabajo desde el teléfono— porque el
              que lo anota está en el puerto con las manos sucias. Duplicar acá
              la carga obligaría a adivinar en cuál de los dos lados se anota.
              Lo único que se escribe es anular un trabajo mal cargado, y
              anular no es borrar. */}
          <Route path="/puerto/fichaje" element={<PuertoFichaje />} />
          <Route path="/choferes-autorizados" element={<ChoferesAutorizados />} />
          <Route path="/puerto/trabajos" element={<PuertoTrabajos />} />
          <Route path="/visitas" element={<Visitas />} />
          <Route path="/vigilancia" element={<Vigilancia />} />
          {/* Las llaves con QR (ticket 522). `etiquetas` va como ruta propia
              antes de `:interno`: react-router elige la estática igual. */}
          <Route path="/llaves" element={<Llaves />} />
          <Route path="/llaves/etiquetas" element={<LlavesEtiquetas />} />
          <Route path="/llaves/:interno" element={<Llave />} />
          <Route path="/puerto/panol" element={<PuertoPanol />} />
          <Route path="/puerto/novedades" element={<PuertoNovedades />} />
          <Route path="/pedidos" element={<PedidosSistema />} />
          <Route path="/urea/historico" element={<Urea />} />
          <Route path="/gasoil" element={<Gasoil />} />
          <Route path="/accesos" element={<Accesos />} />
          <Route path="/espejo" element={<EspejoChats />} />
          <Route path="/alertas" element={<Alertas />} />
          <Route path="/lecturas" element={<Lecturas />} />
          {/* Facturas y remitos: una sola lista, WhatsApp + mail juntos. La
              dirección vieja /comprobantes/mail redirige acá (se compartió). */}
          <Route path="/comprobantes" element={<FacturasRemitos />} />
          <Route path="/comprobantes/mail" element={<Navigate to="/comprobantes" replace />} />
          {/* Proveedores › Facturas a pagar (Nico, 19/10). El sector tiene
              una sola pantalla por ahora: /proveedores lleva ahí. */}
          <Route path="/proveedores/facturas" element={<FacturasAPagar />} />
          <Route path="/proveedores" element={<Navigate to="/proveedores/facturas" replace />} />
          <Route
            path="/aduana/clientes"
            element={
              <Suspense fallback={<p className="tenue">Abriendo Aduana…</p>}>
                <Aduana />
              </Suspense>
            }
          />
          {/* LAS DOS QUE EMITEN PAPELES ENTRAN CON LA VERSIÓN AL DÍA.
              Fabián, 09/10: un MIC salió con la casilla 27 mal desde una
              pestaña de la mañana, con el arreglo publicado a las 11:15.
              Igna, 09/10: «que el Hub se actualice solo al entrar a Aduana ›
              CRT o MIC si hay versión nueva y no hay nada a medio cargar».
              `AlDiaParaEmitir` mira antes de dibujar y, si hace falta,
              recarga en la misma dirección. SÓLO estas dos: el resto del hub
              sigue con el cartel y sin recargar solo — tampoco la hoja de
              prueba (/aduana/uat) ni los papeles del despachante, que no
              emiten nada. */}
          <Route
            path="/aduana/crt"
            element={
              <AlDiaParaEmitir>
                <Suspense fallback={<p className="tenue">Abriendo el CRT…</p>}>
                  <Crt />
                </Suspense>
              </AlDiaParaEmitir>
            }
          />
          <Route
            path="/aduana/mic"
            element={
              <AlDiaParaEmitir>
                <Suspense fallback={<p className="tenue">Abriendo los MIC…</p>}>
                  <Mic />
                </Suspense>
              </AlDiaParaEmitir>
            }
          />
          <Route
            path="/aduana/papeles"
            element={
              <Suspense fallback={<p className="tenue">Abriendo los papeles…</p>}>
                <AduanaPapeles />
              </Suspense>
            }
          />
          <Route
            path="/aduana/uat"
            element={
              <Suspense fallback={<p className="tenue">Abriendo la hoja de prueba…</p>}>
                <CrtUat />
              </Suspense>
            }
          />
          <Route
            path="/seguros"
            element={
              <Suspense fallback={<p className="tenue">Abriendo Seguros…</p>}>
                <Seguros />
              </Suspense>
            }
          />
          <Route
            path="/taller/pedidos-choferes"
            element={
              <Suspense fallback={<p className="tenue">Abriendo los pedidos de los chóferes…</p>}>
                <PedidosChoferes />
              </Suspense>
            }
          />
          {/* El detalle de un ticket de la ticketera, con los repuestos que se
              le colocaron a la unidad (Igna, 22/10: la CantApp vieja se
              retira). Sin ítem en el menú: se llega desde la ficha de la
              unidad o con el link del ticket. */}
          <Route
            path="/taller/ticket/:id"
            element={
              <Suspense fallback={<p className="tenue">Abriendo el ticket…</p>}>
                <TicketTaller />
              </Suspense>
            }
          />
          <Route
            path="/taller/garantias"
            element={
              <Suspense fallback={<p className="tenue">Abriendo Garantías…</p>}>
                <Garantias />
              </Suspense>
            }
          />
          <Route
            path="/pdfs"
            element={
              /* Con `Suspense`, como Presidencia: sin esto React tira el
                 componente perezoso y la pantalla queda en blanco. El fallback
                 dice algo — bajar el motor de PDFs es la primera espera, y una
                 pantalla muda se lee como que no cargó. */
              <Suspense fallback={<p className="tenue">Abriendo el taller…</p>}>
                <Pdfs />
              </Suspense>
            }
          />
          {/* LA ÚLTIMA, Y TIENE QUE ESTAR. Sin comodín, una dirección que no
              existe no da error ni redirige: el `Outlet` queda vacío y el hub
              se dibuja con el medio EN BLANCO, sin una pista de qué pasó.
              Ya se lo comió `/reset-password` en su momento y `/aduana/uat`
              el 26/08/2026. */}
          <Route
            path="*"
            element={
              <Suspense fallback={null}>
                <NoExiste />
              </Suspense>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
